/**
 * WCAG 2.1 colour contrast utilities.
 *
 * Used by SpinWheel to pick a text colour that meets the 4.5:1 minimum
 * contrast ratio (WCAG 1.4.3) against each segment's background.
 */

function hexToLinear(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const linearize = (v: number) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);

  return [linearize(r), linearize(g), linearize(b)];
}

export function getRelativeLuminance(hex: string): number {
  const [r, g, b] = hexToLinear(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function getContrastRatio(hex1: string, hex2: string): number {
  const L1 = getRelativeLuminance(hex1);
  const L2 = getRelativeLuminance(hex2);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Return the text colour (#ffffff or #1a1a2e) that meets 4.5:1 against
 * the given background hex. If neither fully meets 4.5:1, the one with
 * the higher contrast is returned — a best-effort fallback.
 */
export function accessibleTextColor(bgHex: string): string {
  const withWhite = getContrastRatio(bgHex, '#ffffff');
  const withDark = getContrastRatio(bgHex, '#1a1a2e');

  if (withWhite >= 4.5) return '#ffffff';
  if (withDark >= 4.5) return '#1a1a2e';

  // Neither meets 4.5:1 — pick the better one
  return withWhite > withDark ? '#ffffff' : '#1a1a2e';
}
